import React, { useEffect, useRef } from "react";
import { ArrowUpIcon } from "lucide-react";
import { Button } from "@src/ui/button";
import ComposerBrandSelector from "@src/components/assistant-chat/ComposerBrandSelector";
import ChatAttachment, { ChatAttachmentItem } from "../ChatAttachment";
import { ChatAttachMenu } from "../ChatAttachMenu";
import { cn } from "@src/ui/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/ui/tooltip";
import { useAiChatComposerFileUpload } from "@src/hooks/useAiChatComposerFileUpload";

export interface AttachmentProps {
  enableAttachments?: boolean;
  attachments: ChatAttachmentItem[];
  onAttachmentsChanged?: React.Dispatch<
    React.SetStateAction<ChatAttachmentItem[]>
  >;
}

export interface ChatComposerProps {
  input: string;
  onInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit?: (e: React.FormEvent) => void;
  isStreaming?: boolean;
  onStop?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  brandSelectorProps?: React.ComponentProps<typeof ComposerBrandSelector>;
  attachmentProps?: AttachmentProps;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  input,
  onInputChange,
  onSubmit,
  isStreaming = false,
  onStop,
  placeholder = "Ask me anything...",
  disabled = false,
  className = "",
  brandSelectorProps,
  attachmentProps,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { triggerFileUpload, HiddenFileInput } = useAiChatComposerFileUpload({
    onAttachmentsChanged: attachmentProps?.onAttachmentsChanged,
  });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isStreaming && !disabled) {
      onSubmit?.(e);
    }
  };

  // Determine if any attachments are uploading or have errors
  const hasUploading = attachmentProps?.attachments?.some(
    (att) => att.uploadingFile?.state === "uploading",
  );
  const hasError = attachmentProps?.attachments?.some(
    (att) => att.uploadingFile?.state === "error",
  );
  const submitDisabled = !input.trim() || disabled || hasUploading || hasError;
  const submitTooltip = hasUploading
    ? "file upload in progress"
    : hasError
      ? "file uploads failed"
      : undefined;

  return (
    <div
      className={cn(
        "flex flex-col mx-auto w-full max-w-2xl rounded-2xl border border-gray-3  p-4 shadow-sm gap-3",
        className,
        !onSubmit ? "bg-gray-1" : "bg-white",
      )}
    >
      {HiddenFileInput}
      {/* Attachments section */}
      {attachmentProps?.attachments &&
        attachmentProps.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachmentProps.attachments.map((attachment, index) => (
              <ChatAttachment
                key={index}
                {...attachment}
                onRemove={() => {
                  attachmentProps.onAttachmentsChanged?.([
                    ...attachmentProps.attachments.slice(0, index),
                    ...attachmentProps.attachments.slice(index + 1),
                  ]);
                }}
              />
            ))}
          </div>
        )}

      <textarea
        ref={textareaRef}
        value={input}
        onChange={onInputChange}
        placeholder={placeholder}
        rows={1}
        disabled={disabled}
        className={cn(
          "w-full resize-none border-none text-md outline-none placeholder:text-muted-foreground focus:ring-0 disabled:opacity-50 ",
          className,
          onSubmit ? "min-h-10 bg-white" : "bg-gray-1",
        )}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !disabled) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
      />
      {onSubmit && (
        <div className="flex items-center justify-between ">
          <div className="flex items-center gap-2 flex-1">
            {attachmentProps?.enableAttachments && (
              <ChatAttachMenu
                onSelectItem={(item) => {
                  // Add a new attachment for the selected library item
                  const newAttachment = {
                    libraryItemFragment: item.libraryItem,
                  };
                  attachmentProps.onAttachmentsChanged?.([
                    ...attachmentProps.attachments,
                    newAttachment,
                  ]);
                }}
                onUploadClick={triggerFileUpload}
                excludeLibraryItemIds={
                  attachmentProps.attachments
                    .map((att) => att.libraryItemFragment?.id)
                    .filter(Boolean) as string[]
                }
                disabled={disabled}
              />
            )}
            {brandSelectorProps ? (
              <ComposerBrandSelector {...brandSelectorProps} />
            ) : null}
          </div>
          <div className="flex items-center ml-2">
            {isStreaming ? (
              <Button
                type="button"
                variant="default"
                size="icon"
                onClick={onStop}
                disabled={disabled}
                className="size-9 rounded-full border border-gray-3 bg-gray-1 text-gray-8 hover:bg-gray-2"
              >
                <CircleStopIcon />
              </Button>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type="submit"
                      onClick={handleSubmit}
                      variant="default"
                      size="icon"
                      disabled={submitDisabled}
                      className="size-9 rounded-full border border-primary disabled:border-gray-5 bg-primary text-white hover:bg-primary-8 disabled:bg-gray-1 disabled:text-gray-7"
                    >
                      <ArrowUpIcon size={18} />
                    </Button>
                  </span>
                </TooltipTrigger>
                {submitTooltip && (
                  <TooltipContent>{submitTooltip}</TooltipContent>
                )}
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};

export default ChatComposer;
